import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, Target, Rocket, Globe, Shield, 
  CheckCircle, ArrowRight, Mail, Phone, Building2, User,
  Briefcase, MessageSquare, ChevronDown, Zap,
  BarChart3, DollarSign, Smartphone, Store, Calendar,
  Award, Star, Send, Sparkles, Clock, Bell,
  CreditCard, Settings, Heart, Check, X, Monitor,
  Scissors, Dumbbell, Car, Music, Stethoscope, Footprints,
  Palette, ExternalLink, Layers, PieChart, TrendingDown,
  AlertCircle, Repeat
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Logos
const LOGOS = {
  dark: 'https://customer-assets.emergentagent.com/job_store-boost-1/artifacts/p00q8cnd_logo_darkversion.6219654a445e56ed3cd0.png'
};

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById(`counter-${end}-${suffix}-${prefix}`);
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [end, duration, hasAnimated, suffix, prefix]);

  return (
    <span id={`counter-${end}-${suffix}-${prefix}`}>
      {prefix}{count.toLocaleString('it-IT')}{suffix}
    </span>
  );
};

// Custom App Data
const CUSTOM_APPS = [
  { name: 'AppXGym', icon: Dumbbell, color: 'emerald', niche: 'Palestre', url: 'https://appxgym.com' },
  { name: 'AppXBarber', icon: Scissors, color: 'blue', niche: 'Barbieri', url: 'https://appxbarber.com' },
  { name: 'AppXNail', icon: Palette, color: 'pink', niche: 'Nail Art', url: 'https://appxnail.com' },
  { name: 'AppXHair', icon: Scissors, color: 'purple', niche: 'Parrucchieri', url: 'https://appxhair.com' },
  { name: 'AppXVet', icon: Stethoscope, color: 'amber', niche: 'Veterinari', url: 'https://appxvet.com' },
  { name: 'AppXDrive', icon: Car, color: 'red', niche: 'Autoscuole', url: 'https://appxdrive.com' },
  { name: 'AppXDance', icon: Music, color: 'orange', niche: 'Scuole Danza', url: 'https://appxdance.com' },
  { name: 'AppXFisio', icon: Footprints, color: 'cyan', niche: 'Fisioterapisti', url: 'https://appxfisio.com' }
];

const Invest = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    isEntrepreneur: '',
    experience: '',
    investmentCapacity: '',
    whyInvest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Initial load animation
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Scroll animation for sections with staggered delays
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.animate-section');
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.85) {
          // Add staggered delay based on index
          setTimeout(() => {
            section.classList.add('animate-in');
          }, index * 50);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/investor-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        alert(data.error || 'Errore durante l\'invio. Riprova.');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Errore di connessione. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white overflow-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-[#0a0f1a]/90 backdrop-blur-xl border-b border-slate-800 transition-all duration-500 ${isLoaded ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img src={LOGOS.dark} alt="Skipres" className="h-8 transition-transform duration-300 hover:scale-105" />
          <a href="#contact-form">
            <Button className="bg-emerald-500 hover:bg-emerald-400 text-white transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/30">
              Diventa Partner
            </Button>
          </a>
        </div>
      </nav>

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-screen flex items-center pt-24 pb-12 overflow-hidden">
        {/* Background Effects - Animated */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-emerald-500/5 via-transparent to-blue-500/5"></div>
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '4s' }}></div>
          <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s', animationDuration: '4s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/5 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s', animationDuration: '5s' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center">
            
            {/* LEFT COLUMN - Text Content */}
            <div className={`order-1 lg:order-1 transition-all duration-1000 ${isLoaded ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'}`}>
              <div className={`inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 mb-6 transition-all duration-700 delay-200 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                <Sparkles className="w-4 h-4 text-emerald-400 animate-pulse" />
                <span className="text-emerald-400 text-sm font-medium">Opportunità di Investimento Esclusiva</span>
              </div>

              <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight transition-all duration-700 delay-300 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                Investi nell&apos;Ecosistema che 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400 animate-gradient bg-[length:200%_auto]"> Rivoluziona </span>
                il Commercio Locale
              </h1>

              <p className={`text-lg text-slate-300 mb-4 transition-all duration-700 delay-400 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                <strong className="text-white">Skipres</strong> non è solo un&apos;app. È un ecosistema completo: 
                <span className="text-emerald-400"> Marketplace</span> per prenotazioni e acquisti, 
                <span className="text-blue-400"> Gestionale Cloud</span>, 
                <span className="text-purple-400"> App White-Label</span> e 
                <span className="text-amber-400"> Network di Partner</span>.
              </p>

              <p className={`text-slate-400 mb-8 transition-all duration-700 delay-500 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                <strong className="text-white">Qualsiasi servizio, qualsiasi prodotto</strong> — tutto in un&apos;unica piattaforma.
                Cerchiamo <strong className="text-emerald-400">UN unico partner strategico</strong> che creda nel progetto.
              </p>

              <div className={`mb-8 transition-all duration-700 delay-[600ms] ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
                <a href="#contact-form">
                  <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-white px-10 py-7 rounded-2xl shadow-xl shadow-emerald-500/30 group w-full sm:w-auto transition-all duration-300 hover:scale-105 hover:shadow-emerald-500/50">
                    <div className="flex items-center gap-3">
                      <div className="text-left">
                        <span className="block text-xl font-bold">Parla con il Fondatore</span>
                        <span className="block text-sm text-emerald-100 font-normal">Alessandro Soviero, CEO & Founder</span>
                      </div>
                      <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </Button>
                </a>
              </div>

              {/* Quick Stats with staggered animation */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: '+2M', label: 'Prenotazioni', color: 'emerald', delay: '700ms' },
                  { value: '100+', label: 'Abbonati', color: 'blue', delay: '800ms' },
                  { value: '€20K', label: 'Fatturato Anno 1', color: 'purple', delay: '900ms' }
                ].map((stat, i) => (
                  <div 
                    key={i} 
                    className={`text-center p-3 rounded-xl border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:border-${stat.color}-500/50 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
                    style={{ transitionDelay: stat.delay }}
                  >
                    <p className={`text-2xl font-bold text-${stat.color}-400`}>{stat.value}</p>
                    <p className="text-slate-500 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT COLUMN - Mockups */}
            <div className={`order-2 lg:order-2 relative transition-all duration-1000 delay-300 ${isLoaded ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'}`}>
              {/* Mockups Container */}
              <div className="relative h-[650px]">
                
                {/* PHONE 1 - Marketplace Search (Top Left) */}
                <div className={`absolute left-0 top-0 z-30 transform hover:scale-105 transition-all duration-500 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{ transitionDelay: '400ms' }}>
                  <div className="w-[180px] bg-gradient-to-b from-slate-800 to-slate-900 rounded-[2rem] p-1.5 shadow-2xl shadow-black/50 hover:shadow-emerald-500/20 transition-shadow duration-300">
                    <div className="bg-white rounded-[1.7rem] h-[360px] overflow-hidden">
                      {/* Status Bar */}
                      <div className="bg-white px-4 py-1.5 flex justify-between items-center text-[10px] text-slate-400">
                        <span>9:41</span>
                        <div className="w-12 h-4 bg-black rounded-full"></div>
                        <span>100%</span>
                      </div>
                      
                      {/* Logo */}
                      <div className="px-4 pt-3 pb-2">
                        <div className="text-xl font-bold">
                          <span className="text-slate-900">Skip</span><span className="text-emerald-500">res</span>
                        </div>
                      </div>

                      {/* Search Fields */}
                      <div className="px-3 space-y-2">
                        <div className="border border-slate-200 rounded-lg px-3 py-2">
                          <p className="text-slate-400 text-[9px]">Cosa?</p>
                          <p className="text-slate-700 text-[11px]">Parrucchiere, ristorante...</p>
                        </div>
                        <div className="border border-slate-200 rounded-lg px-3 py-2">
                          <p className="text-slate-400 text-[9px]">Dove?</p>
                          <p className="text-slate-700 text-[11px]">Milano, Italia</p>
                        </div>
                        <button className="w-full bg-emerald-500 text-white py-2.5 rounded-lg text-xs font-semibold">
                          Cerca
                        </button>
                      </div>

                      {/* Categories */}
                      <div className="px-3 mt-3">
                        <p className="text-slate-400 text-[9px] mb-2">Categorie</p>
                        <div className="flex flex-wrap gap-1">
                          {['Barbieri', 'Estetisti', 'Palestre'].map((cat, i) => (
                            <span key={i} className="bg-slate-100 text-slate-600 text-[9px] px-2 py-1 rounded-full">{cat}</span>
                          ))}
                        </div>
                      </div>

                      {/* Tagline */}
                      <div className="px-3 mt-4">
                        <p className="text-slate-800 font-bold text-sm leading-tight">Cosa vuoi,</p>
                        <p className="text-slate-800 font-bold text-sm leading-tight">dove vuoi,</p>
                        <p className="text-emerald-500 font-bold text-sm leading-tight">quando puoi.</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-emerald-400 text-xs sm:text-sm mt-2 font-bold tracking-wide animate-pulse">MARKETPLACE</p>
                </div>

                {/* LAPTOP - Gestionale (Center) */}
                <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 transition-all duration-700 ${isLoaded ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`} style={{ transitionDelay: '600ms' }}>
                  <div className="w-[320px] animate-float" style={{ animationDuration: '6s' }}>
                    {/* Screen */}
                    <div className="bg-slate-800 rounded-t-xl p-2 border-t border-x border-slate-600">
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <div className="flex-1 flex justify-center">
                          <div className="bg-slate-700 rounded px-3 py-0.5 text-slate-400 text-[8px]">dashboard.skipres.com</div>
                        </div>
                      </div>
                      
                      {/* Dashboard Content */}
                      <div className="bg-slate-900 rounded-lg p-3 h-[200px]">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-bold">
                              <span className="text-white">Skip</span><span className="text-emerald-400">res</span>
                            </div>
                            <span className="text-slate-400 text-[9px]">Gestionale</span>
                          </div>
                          <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">A</div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2 text-center">
                            <p className="text-emerald-400 text-sm font-bold">47</p>
                            <p className="text-slate-500 text-[7px]">Prenotazioni</p>
                          </div>
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 text-center">
                            <p className="text-blue-400 text-sm font-bold">€1.8K</p>
                            <p className="text-slate-500 text-[7px]">Incasso</p>
                          </div>
                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-2 text-center">
                            <p className="text-purple-400 text-sm font-bold">12</p>
                            <p className="text-slate-500 text-[7px]">Nuovi Clienti</p>
                          </div>
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-center">
                            <p className="text-amber-400 text-sm font-bold">4.9</p>
                            <p className="text-slate-500 text-[7px]">Rating</p>
                          </div>
                        </div>

                        {/* Chart + Calendar */}
                        <div className="grid grid-cols-2 gap-2">
                          {/* Mini Chart */}
                          <div className="bg-slate-800/50 rounded-lg p-2">
                            <p className="text-slate-400 text-[8px] mb-1">Vendite Mensili</p>
                            <div className="flex items-end gap-0.5 h-12">
                              {[30, 45, 35, 60, 50, 70, 55, 80, 65, 90, 75, 95].map((h, i) => (
                                <div key={i} className="flex-1 bg-gradient-to-t from-emerald-500 to-blue-500 rounded-t" style={{ height: `${h}%` }}></div>
                              ))}
                            </div>
                          </div>
                          {/* Mini Calendar */}
                          <div className="bg-slate-800/50 rounded-lg p-2">
                            <p className="text-slate-400 text-[8px] mb-1">Prossimi</p>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-[8px]">
                                <span className="text-emerald-400">14:00</span>
                                <span className="text-white">Maria R.</span>
                              </div>
                              <div className="flex items-center gap-1 text-[8px]">
                                <span className="text-emerald-400">15:30</span>
                                <span className="text-white">Luca B.</span>
                              </div>
                              <div className="flex items-center gap-1 text-[8px]">
                                <span className="text-emerald-400">16:00</span>
                                <span className="text-white">Sofia V.</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Laptop Base */}
                    <div className="bg-slate-700 h-3 rounded-b-xl"></div>
                    <div className="bg-slate-600 h-1.5 w-1/3 mx-auto rounded-b-lg"></div>
                  </div>
                  <p className="text-center text-blue-400 text-xs sm:text-sm mt-2 font-bold tracking-wide animate-pulse" style={{ animationDelay: '0.5s' }}>GESTIONALE CLOUD</p>
                </div>

                {/* PHONE 2 - App Personalizzata (Bottom Right) */}
                <div className={`absolute right-0 bottom-0 z-30 transform hover:scale-105 transition-all duration-500 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{ transitionDelay: '800ms' }}>
                  <div className="w-[170px] bg-gradient-to-b from-slate-800 to-slate-900 rounded-[2rem] p-1.5 shadow-2xl shadow-black/50 hover:shadow-purple-500/20 transition-shadow duration-300">
                    <div className="bg-gradient-to-b from-purple-900 to-slate-900 rounded-[1.7rem] h-[340px] overflow-hidden">
                      {/* Status Bar */}
                      <div className="px-4 py-1.5 flex justify-between items-center text-[10px] text-purple-300">
                        <span>9:41</span>
                        <div className="w-12 h-4 bg-black rounded-full"></div>
                        <span>100%</span>
                      </div>
                      
                      {/* App Header */}
                      <div className="px-3 pt-2 pb-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Dumbbell className="w-4 h-4 text-purple-400" />
                          <span className="text-white text-sm font-bold">AppXGym</span>
                        </div>
                        <span className="bg-purple-500/30 text-purple-300 text-[8px] px-1.5 py-0.5 rounded">PRO</span>
                      </div>

                      {/* User Card */}
                      <div className="px-3 mb-2">
                        <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-2">
                          <p className="text-purple-300 text-[9px]">Il tuo allenamento</p>
                          <p className="text-white text-xs font-bold">Giorno 3 - Spalle</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-1 bg-slate-700 rounded-full">
                              <div className="w-2/3 h-full bg-purple-500 rounded-full"></div>
                            </div>
                            <span className="text-purple-400 text-[9px]">67%</span>
                          </div>
                        </div>
                      </div>

                      {/* Exercise List */}
                      <div className="px-3">
                        <p className="text-slate-400 text-[9px] mb-1.5">Esercizi</p>
                        <div className="space-y-1.5">
                          {[
                            { name: 'Military Press', done: true },
                            { name: 'Alzate Laterali', done: true },
                            { name: 'Face Pull', done: false }
                          ].map((ex, i) => (
                            <div key={i} className={`flex items-center justify-between rounded-lg p-1.5 ${ex.done ? 'bg-purple-500/20' : 'bg-slate-800/50'}`}>
                              <span className="text-white text-[10px]">{ex.name}</span>
                              {ex.done ? (
                                <Check className="w-3 h-3 text-purple-400" />
                              ) : (
                                <div className="w-3 h-3 border border-slate-600 rounded"></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="px-3 mt-3">
                        <button className="w-full bg-purple-500 text-white py-2 rounded-lg text-[10px] font-semibold">
                          Prenota Lezione
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-purple-400 text-xs sm:text-sm mt-2 font-bold tracking-wide animate-pulse" style={{ animationDelay: '1s' }}>APP WHITE-LABEL</p>
                </div>

                {/* EARNINGS CHARTS - Programma Elit */}
                <div className={`absolute right-0 top-8 z-10 transition-all duration-700 ${isLoaded ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'}`} style={{ transitionDelay: '1000ms' }}>
                  <div className="bg-slate-800/90 backdrop-blur border border-amber-500/30 rounded-xl p-3 w-[140px] hover:scale-105 hover:border-amber-500/50 transition-all duration-300">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Award className="w-3 h-3 text-amber-400" />
                      <span className="text-amber-400 text-[9px] font-medium">Programma Elit</span>
                    </div>
                    {/* Pie Chart Visual */}
                    <div className="relative w-16 h-16 mx-auto mb-2">
                      <svg viewBox="0 0 36 36" className="w-full h-full">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#1e293b"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#f59e0b"
                          strokeWidth="3"
                          strokeDasharray="20, 100"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-amber-400 text-sm font-bold">20%</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-[8px] text-center">Commissione vendite</p>
                  </div>
                </div>

                {/* Revenue Chart */}
                <div className={`absolute left-0 bottom-24 z-10 transition-all duration-700 ${isLoaded ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'}`} style={{ transitionDelay: '1100ms' }}>
                  <div className="bg-slate-800/90 backdrop-blur border border-emerald-500/30 rounded-xl p-3 w-[150px] hover:scale-105 hover:border-emerald-500/50 transition-all duration-300">
                    <div className="flex items-center gap-1.5 mb-2">
                      <TrendingUp className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-400 text-[9px] font-medium">Revenue Growth</span>
                    </div>
                    {/* Bar Chart */}
                    <div className="flex items-end gap-1 h-12 mb-1">
                      {[
                        { h: 20, label: 'Q1' },
                        { h: 45, label: 'Q2' },
                        { h: 65, label: 'Q3' },
                        { h: 90, label: 'Q4' }
                      ].map((bar, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center">
                          <div className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t" style={{ height: `${bar.h}%` }}></div>
                          <span className="text-slate-500 text-[7px] mt-0.5">{bar.label}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-white text-[10px] font-bold text-center">+350% YoY</p>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ==================== COSA È SKIPRES ==================== */}
      <section className="py-12 md:py-24 bg-gradient-to-b from-[#0a0f1a] to-slate-900/50 animate-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-emerald-400 font-medium mb-4 block">L&apos;ECOSISTEMA</span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Cinque Prodotti, <span className="text-emerald-400">Due Mercati</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-3xl mx-auto">
              Monetizziamo sia dalle <strong className="text-white">aziende</strong> (B2B) che dagli 
              <strong className="text-white"> utenti</strong> (B2C). Ogni prodotto genera ricavi ricorrenti 
              e insieme formano un ecosistema integrato completo.
            </p>
          </div>

          {/* B2B Section Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <Store className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Per le Aziende</h3>
                <p className="text-slate-400 text-sm">Prodotti B2B per professionisti e attività locali</p>
              </div>
            </div>
          </div>

          {/* B2B Product Cards */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-16">
            {/* Product 1: Start */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8 hover:border-emerald-500/50 transition-all group">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 mb-3">
                    <Target className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 text-xs font-medium">VISIBILITÀ</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white">Start Skipres</h3>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-sm line-through">€9,99/mese</p>
                  <p className="text-2xl font-bold text-emerald-400">€4,99<span className="text-sm text-slate-400">/mese</span></p>
                </div>
              </div>
              <p className="text-slate-300 mb-6">
                Vetrina online nel marketplace Skipres. Le attività vengono trovate dai clienti, 
                ricevono prenotazioni automatiche e recensioni.
              </p>
              {/* Mini Mockup */}
              <div className="bg-slate-900 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-xl flex items-center justify-center">
                    <Scissors className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Barber Shop Milano</p>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />)}
                      <span className="text-slate-400 text-xs ml-1">(127)</span>
                    </div>
                  </div>
                </div>
                <Button className="w-full bg-emerald-500/20 text-emerald-400 text-sm py-2 border border-emerald-500/30">
                  Prenota Ora
                </Button>
              </div>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Prenotazioni automatiche • Recensioni • SEO Local</span>
              </div>
            </div>

            {/* Product 2: Gestionale */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8 hover:border-blue-500/50 transition-all group">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-3 py-1 mb-3">
                    <BarChart3 className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400 text-xs font-medium">GESTIONE</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white">Gestionale Skipres</h3>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-400">€19,90 - €29,90<span className="text-sm text-slate-400">/mese</span></p>
                  <p className="text-slate-500 text-xs">Annuale: €214,90 - €290</p>
                </div>
              </div>
              <p className="text-slate-300 mb-6">
                Software cloud completo per gestire appuntamenti, clienti, pagamenti, statistiche 
                e comunicazioni automatiche.
              </p>
              {/* Mini Dashboard Mockup */}
              <div className="bg-slate-900 rounded-xl p-4 mb-6">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-emerald-500/10 rounded-lg p-2 text-center">
                    <p className="text-emerald-400 text-lg font-bold">24</p>
                    <p className="text-slate-500 text-xs">Oggi</p>
                  </div>
                  <div className="bg-blue-500/10 rounded-lg p-2 text-center">
                    <p className="text-blue-400 text-lg font-bold">€890</p>
                    <p className="text-slate-500 text-xs">Incasso</p>
                  </div>
                  <div className="bg-purple-500/10 rounded-lg p-2 text-center">
                    <p className="text-purple-400 text-lg font-bold">156</p>
                    <p className="text-slate-500 text-xs">Clienti</p>
                  </div>
                </div>
                <div className="h-12 bg-slate-800 rounded flex items-end p-1 gap-0.5">
                  {[40, 65, 45, 80, 55, 70, 90].map((h, i) => (
                    <div key={i} className="flex-1 bg-gradient-to-t from-blue-500 to-emerald-500 rounded-sm" style={{ height: `${h}%` }}></div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                <span>100 abbonati attivi • €20K fatturato primo anno</span>
              </div>
            </div>

            {/* Product 3: App Custom */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8 hover:border-purple-500/50 transition-all group">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-3 py-1 mb-3">
                    <Smartphone className="w-4 h-4 text-purple-400" />
                    <span className="text-purple-400 text-xs font-medium">WHITE-LABEL</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white">App Personalizzate</h3>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-purple-400">€49 - €149<span className="text-sm text-slate-400">/mese</span></p>
                  <p className="text-slate-500 text-xs">o licenza a vita da €2.457</p>
                </div>
              </div>
              <p className="text-slate-300 mb-6">
                App native iOS/Android completamente brandizzate. 8 nicchie già lanciate, 
                infinite possibilità di espansione.
              </p>
              {/* App Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
                {CUSTOM_APPS.map((app, i) => (
                  <a 
                    key={i}
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`bg-${app.color}-500/10 border border-${app.color}-500/30 rounded-lg p-2 text-center hover:scale-105 transition-transform`}
                  >
                    <app.icon className={`w-5 h-5 text-${app.color}-400 mx-auto mb-1`} />
                    <p className="text-slate-300 text-[9px]">{app.niche}</p>
                  </a>
                ))}
              </div>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <CheckCircle className="w-4 h-4 text-purple-400" />
                <span>5 app vendute • Scalabile a qualsiasi nicchia</span>
              </div>
            </div>

            {/* Product 4: Elit */}
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-3xl p-8 hover:border-amber-500/50 transition-all group">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 mb-3">
                    <Award className="w-4 h-4 text-amber-400" />
                    <span className="text-amber-400 text-xs font-medium">NETWORK</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-2xl font-bold text-white">Programma Elit</h3>
                    <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-2 py-1 rounded-full border border-amber-500/30 animate-pulse">IN SVILUPPO</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-amber-400">20%</p>
                  <p className="text-slate-500 text-xs">su ogni transazione</p>
                </div>
              </div>
              <p className="text-slate-300 mb-6">
                Network di partner che portano attività su Skipres. Commissione su ogni 
                pagamento online - ricavi passivi scalabili.
              </p>
              {/* Revenue Potential */}
              <div className="bg-slate-900/50 rounded-xl p-4 mb-6 text-center">
                <p className="text-slate-400 text-sm mb-2">Con 1.000 attività Elit</p>
                <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                  €100K/mese
                </p>
                <p className="text-slate-500 text-xs">commissioni potenziali</p>
              </div>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <CheckCircle className="w-4 h-4 text-amber-400" />
                <span>Ricavi passivi • Zero costi acquisizione</span>
              </div>
            </div>
          </div>

          {/* B2C Section Header */}
          <div className="mb-8 mt-16">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Per gli Utenti</h3>
                <p className="text-slate-400 text-sm">Prodotti B2C per monetizzare direttamente la base utenti</p>
              </div>
            </div>
          </div>

          {/* B2C Product Card - Calendar Pro */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-3xl p-8 hover:border-cyan-500/50 transition-all group">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-3 py-1 mb-3">
                    <Calendar className="w-4 h-4 text-cyan-400" />
                    <span className="text-cyan-400 text-xs font-medium">ORGANIZZAZIONE</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-2xl font-bold text-white">Skipres Calendar Pro</h3>
                    <span className="bg-cyan-500/20 text-cyan-400 text-xs font-bold px-2 py-1 rounded-full border border-cyan-500/30 animate-pulse">IN SVILUPPO</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-cyan-400">€9,99<span className="text-sm text-slate-400">/anno</span></p>
                  <p className="text-slate-500 text-xs">o €0,99/mese</p>
                </div>
              </div>
              <p className="text-slate-300 mb-6">
                Trasforma Skipres nel <strong className="text-white">calendario personale intelligente</strong> dell&apos;utente. 
                Non solo vede le prenotazioni, ma può aggiungere task personali e ricevere avvisi sui conflitti.
              </p>
              
              {/* Calendar Mockup */}
              <div className="bg-slate-900/50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-white text-sm font-medium">Gennaio 2026</p>
                  <div className="flex gap-1">
                    <ChevronDown className="w-4 h-4 text-slate-400 rotate-90" />
                    <ChevronDown className="w-4 h-4 text-slate-400 -rotate-90" />
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
                  {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((d, i) => (
                    <span key={i} className="text-slate-500">{d}</span>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1 text-center">
                  {[...Array(31)].map((_, i) => {
                    const day = i + 1;
                    const hasBooking = [8, 15, 22].includes(day);
                    const hasTask = [10, 15, 20].includes(day);
                    const isConflict = day === 15;
                    return (
                      <div 
                        key={i} 
                        className={`text-xs py-1 rounded relative ${
                          isConflict ? 'bg-red-500/30 text-red-400 ring-2 ring-red-500/50' :
                          hasBooking && hasTask ? 'bg-cyan-500/30 text-cyan-400' :
                          hasBooking ? 'bg-emerald-500/20 text-emerald-400' :
                          hasTask ? 'bg-purple-500/20 text-purple-400' :
                          'text-slate-400'
                        }`}
                      >
                        {day}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center gap-4 mt-3 text-[10px]">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-slate-400">Prenotazioni</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="text-slate-400">Task personali</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-slate-400">Conflitto</span>
                  </div>
                </div>
              </div>

              {/* Alert Example */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-400 text-xs font-medium">Avviso Conflitto</p>
                    <p className="text-slate-300 text-xs">Vuoi prenotare alle 15:00, ma hai già &quot;Spesa al supermercato&quot; in programma.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {[
                  'Calendario visuale con tutte le prenotazioni',
                  'Aggiungi task personali (spesa, impegni, promemoria)',
                  'Sincronizzazione in tempo reale',
                  'Avvisi intelligenti sui conflitti',
                  'L\'utente usa Skipres ogni giorno'
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Revenue Potential B2C */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8">
              <h4 className="text-xl font-bold text-white mb-6">Potenziale di Revenue B2C</h4>
              
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400">Base utenti target</span>
                    <span className="text-white font-bold">500.000</span>
                  </div>
                  <div className="w-full h-2 bg-slate-700 rounded-full">
                    <div className="w-full h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400">Conversione stimata (5%)</span>
                    <span className="text-cyan-400 font-bold">25.000 abbonati</span>
                  </div>
                  <div className="w-full h-2 bg-slate-700 rounded-full">
                    <div className="w-1/5 h-full bg-cyan-500 rounded-full"></div>
                  </div>
                </div>

                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-sm mb-1">Revenue Annuale Potenziale</p>
                  <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                    €250K/anno
                  </p>
                  <p className="text-slate-500 text-xs mt-1">25.000 × €9,99/anno</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium text-sm">Aumenta l&apos;engagement</p>
                      <p className="text-slate-400 text-xs">L&apos;utente apre l&apos;app ogni giorno per gestire i suoi impegni</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Repeat className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium text-sm">Riduce il churn</p>
                      <p className="text-slate-400 text-xs">Più l&apos;utente dipende dal calendario, meno abbandona</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium text-sm">Upsell naturale</p>
                      <p className="text-slate-400 text-xs">Utenti free → Calendar Pro quando vedono il valore</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PERCHÉ IL 10% ==================== */}
      <section className="py-12 md:py-24 animate-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-emerald-500/5 to-blue-500/5 border border-emerald-500/20 rounded-3xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center">
              <div>
                <span className="text-emerald-400 font-medium mb-4 block">LA VALUTAZIONE</span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Perché €1M per il <span className="text-emerald-400">10%</span>?
                </h2>
                <p className="text-slate-300 text-lg mb-6">
                  Potresti pensare: &ldquo;€20K di fatturato e chiedete €10M di valutazione?&rdquo;
                  Lascia che ti spieghi perché questa è un&apos;opportunità unica.
                </p>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <TrendingDown className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-1">€20K senza marketing</h4>
                      <p className="text-slate-400 text-sm">
                        Quel fatturato viene <strong className="text-white">solo dal gestionale</strong>, 
                        con meno di €500 in pubblicità. Zero marketing strutturato.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Rocket className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-1">Prodotti pronti, non lanciati</h4>
                      <p className="text-slate-400 text-sm">
                        Start, App Custom, Elit, Calendar Pro - sviluppati o in sviluppo, 
                        <strong className="text-white"> ancora da pubblicizzare</strong>. Il potenziale è intatto.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Layers className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-1">Ecosistema completo</h4>
                      <p className="text-slate-400 text-sm">
                        Non investi in un prodotto, ma in <strong className="text-white">5 linee di business</strong> 
                        {' '}(4 B2B + 1 B2C) con ricavi ricorrenti.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-1">Scalabilità globale</h4>
                      <p className="text-slate-400 text-sm">
                        Multilingua già integrato. Ogni attività locale nel mondo è un potenziale cliente. 
                        <strong className="text-white"> TAM infinito</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison */}
              <div>
                <div className="bg-slate-800/50 rounded-2xl p-6 mb-6">
                  <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-emerald-400" />
                    Il tuo 10% tra 5 anni
                  </h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Scenario conservativo</span>
                      <span className="text-emerald-400 font-bold">€5M+ valore</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Scenario realistico</span>
                      <span className="text-blue-400 font-bold">€20M+ valore</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Exit/IPO scenario</span>
                      <span className="text-purple-400 font-bold">€50M+ valore</span>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6">
                  <p className="text-white font-medium mb-2">
                    &ldquo;Ma perché solo il 10%?&rdquo;
                  </p>
                  <p className="text-slate-300 text-sm">
                    Perché crediamo nel progetto. Non vogliamo diluire troppo per poi perdere 
                    la motivazione. Con il 90% in mano ai fondatori, siamo <strong className="text-white">
                    allineati al 100%</strong> con te: se vinciamo, vinciamo insieme. Grande.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== STRATEGIA DI CRESCITA ==================== */}
      <section className="py-12 md:py-24 bg-slate-900/30 animate-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-emerald-400 font-medium mb-4 block">IL PIANO</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Strategia di <span className="text-emerald-400">Crescita</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-3xl mx-auto">
              Un piano in 3 step interconnessi: ogni fase alimenta la successiva, 
              creando un ciclo virtuoso di crescita esponenziale.
            </p>
          </div>

          {/* Step 1: Acquisizione Aziende */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-xl">1</div>
              <h3 className="text-2xl font-bold text-white">Acquisizione Aziende</h3>
            </div>
            <div className="bg-gradient-to-r from-emerald-500/5 to-emerald-500/0 border border-emerald-500/20 rounded-3xl p-8 md:p-10">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 mb-4">
                    <Store className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 text-sm font-medium">FONDAMENTO DELLA CRESCITA</span>
                  </div>
                  <p className="text-slate-300 text-lg mb-6">
                    Portiamo le attività locali nel nostro ecosistema con un prezzo d&apos;ingresso 
                    irresistibile. <strong className="text-white">Start a €4,99/mese</strong> — zero barriere.
                    Una volta dentro, le guidiamo verso prodotti premium.
                  </p>
                  
                  <div className="space-y-4 mb-6">
                    <h4 className="text-white font-bold">La strategia:</h4>
                    <div className="space-y-3">
                      {[
                        { title: 'Vetrina Start', desc: 'Ingresso facile nel marketplace a €4,99/mese' },
                        { title: 'Programma Elit', desc: 'Traffico garantito dalle nostre campagne pubblicitarie' },
                        { title: 'Upsell naturale', desc: 'Da Start → Gestionale → App Custom (crescita ARPU)' },
                        { title: 'Network Effect', desc: 'Più aziende = più valore per gli utenti' }
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-3 text-slate-300">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-white font-medium">{item.title}:</span> {item.desc}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Funnel Visualization */}
                <div className="bg-slate-800/50 rounded-2xl p-6">
                  <h4 className="text-white font-bold mb-6 text-center">Customer Journey Aziende</h4>
                  <div className="space-y-3">
                    {[
                      { stage: 'Scoperta', users: '10.000', color: 'emerald', pct: '100%' },
                      { stage: 'Prova Start (€4,99)', users: '2.000', color: 'emerald', pct: '70%' },
                      { stage: 'Upgrade Gestionale', users: '600', color: 'blue', pct: '45%' },
                      { stage: 'App Custom', users: '120', color: 'purple', pct: '25%' }
                    ].map((item, i) => (
                      <div key={i} className="relative">
                        <div className={`bg-${item.color}-500/10 border border-${item.color}-500/30 rounded-xl p-4`} style={{ width: item.pct }}>
                          <div className="flex justify-between items-center">
                            <span className="text-white font-medium text-sm">{item.stage}</span>
                            <span className={`text-${item.color}-400 font-bold`}>{item.users}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-700 text-center">
                    <p className="text-slate-400 text-sm">LTV medio per azienda</p>
                    <p className="text-emerald-400 text-2xl font-bold">€890/anno</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Arrow connector */}
          <div className="flex justify-center my-6">
            <div className="w-1 h-12 bg-gradient-to-b from-emerald-500 to-blue-500 rounded-full"></div>
          </div>

          {/* Step 2: Crescita Utenti */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">2</div>
              <h3 className="text-2xl font-bold text-white">Crescita Utenti</h3>
            </div>
            <div className="bg-gradient-to-r from-blue-500/5 to-blue-500/0 border border-blue-500/20 rounded-3xl p-8 md:p-10">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-4">
                    <Users className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400 text-sm font-medium">ACCELERATORE DI VALORE</span>
                  </div>
                  <p className="text-slate-300 text-lg mb-6">
                    Investiamo in pubblicità mirata per portare <strong className="text-white">nuovi utenti</strong> che 
                    cercano servizi e prodotti. Ogni nuovo utente aumenta il valore per le aziende partner,
                    incentivandole a restare e crescere con noi.
                  </p>
                  
                  <div className="space-y-4 mb-6">
                    <h4 className="text-white font-bold">Canali di acquisizione:</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { channel: 'Google Ads', budget: '€120K', cpa: '€2.50', icon: Target },
                        { channel: 'Meta Ads', budget: '€100K', cpa: '€1.80', icon: Users },
                        { channel: 'Influencer', budget: '€50K', cpa: '€3.20', icon: Star },
                        { channel: 'SEO/Content', budget: '€30K', cpa: '€0.90', icon: Globe }
                      ].map((item, i) => (
                        <div key={i} className="bg-slate-800/50 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <item.icon className="w-4 h-4 text-blue-400" />
                            <span className="text-white font-medium text-sm">{item.channel}</span>
                          </div>
                          <p className="text-slate-400 text-xs">Budget: {item.budget}</p>
                          <p className="text-blue-400 text-xs font-medium">CPA: {item.cpa}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Growth Projection */}
                <div className="bg-slate-800/50 rounded-2xl p-6">
                  <h4 className="text-white font-bold mb-6 text-center">Proiezione Utenti (12 mesi)</h4>
                  <div className="space-y-4">
                    {[
                      { month: 'Mese 1-3', users: '25.000', growth: 'Base organica' },
                      { month: 'Mese 4-6', users: '80.000', growth: '+220%' },
                      { month: 'Mese 7-9', users: '200.000', growth: '+150%' },
                      { month: 'Mese 10-12', users: '500.000', growth: '+150%' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                        <div>
                          <p className="text-white font-medium">{item.month}</p>
                          <p className="text-slate-500 text-xs">{item.growth}</p>
                        </div>
                        <p className="text-blue-400 font-bold text-xl">{item.users}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-4 border-t border-slate-700 text-center">
                    <p className="text-slate-400 text-sm">Obiettivo Anno 1</p>
                    <p className="text-blue-400 text-3xl font-bold">500K+ Utenti</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Arrow connector */}
          <div className="flex justify-center my-6">
            <div className="w-1 h-12 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
          </div>

          {/* Step 3: Programma Star */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl">3</div>
              <div className="flex items-center gap-3">
                <h3 className="text-2xl font-bold text-white">Programma Star</h3>
                <span className="bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-1 rounded-full border border-purple-500/30 animate-pulse">IN SVILUPPO</span>
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-500/5 to-pink-500/5 border border-purple-500/20 rounded-3xl p-8 md:p-10">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-4">
                    <Star className="w-4 h-4 text-purple-400" />
                    <span className="text-purple-400 text-sm font-medium">MOLTIPLICATORE VIRALE</span>
                  </div>
                  <p className="text-slate-300 text-lg mb-6">
                    Trasformiamo ogni utente in un potenziale <strong className="text-white">influencer</strong>. 
                    Chi condivide prodotti e servizi delle aziende Elit guadagna 
                    <strong className="text-purple-400"> 1-3%</strong> su ogni vendita generata.
                    Marketing a costo zero dopo l&apos;attivazione.
                  </p>
                  
                  <div className="space-y-4 mb-6">
                    <h4 className="text-white font-bold">Come funziona:</h4>
                    <div className="space-y-3">
                      {[
                        'L\'utente si iscrive al Programma Star (gratuito)',
                        'Condivide link ai prodotti/servizi delle aziende Elit',
                        'Ogni vendita tracciata genera commissione 1-3%',
                        'Livelli crescenti: più vendi, più guadagni',
                        'Scalabile fino a influencer con milioni di follower'
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-3 text-slate-300">
                          <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-purple-400 text-xs font-bold">{i + 1}</span>
                          </div>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Star Program Visual */}
                <div className="bg-slate-800/50 rounded-2xl p-6">
                  <h4 className="text-white font-bold mb-6 text-center">Livelli Programma Star</h4>
                  <div className="space-y-4">
                    {[
                      { level: 'Star', commission: '1%', sales: '0-10', color: 'slate' },
                      { level: 'Super Star', commission: '1.5%', sales: '11-50', color: 'blue' },
                      { level: 'Mega Star', commission: '2%', sales: '51-200', color: 'purple' },
                      { level: 'Ultra Star', commission: '3%', sales: '200+', color: 'amber' }
                    ].map((tier, i) => (
                      <div key={i} className={`flex items-center justify-between bg-${tier.color}-500/10 border border-${tier.color}-500/30 rounded-xl p-4`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 bg-${tier.color}-500/20 rounded-full flex items-center justify-center`}>
                            <Star className={`w-5 h-5 text-${tier.color}-400`} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{tier.level}</p>
                            <p className="text-slate-500 text-xs">{tier.sales} vendite/mese</p>
                          </div>
                        </div>
                        <p className={`text-${tier.color}-400 font-bold text-xl`}>{tier.commission}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-slate-700 text-center">
                    <p className="text-slate-400 text-sm">Con 10.000 Star attivi</p>
                    <p className="text-purple-400 text-3xl font-bold">Vendite x10</p>
                    <p className="text-slate-500 text-xs">senza costi pubblicitari aggiuntivi</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Flywheel Effect */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-white mb-8">L&apos;Effetto Volano</h3>
            <div className="flex flex-wrap justify-center items-center gap-4">
              {[
                { text: 'Più Aziende', icon: Store, color: 'emerald' },
                { text: '→', icon: null },
                { text: 'Più Utenti', icon: Users, color: 'blue' },
                { text: '→', icon: null },
                { text: 'Più Star', icon: Star, color: 'purple' },
                { text: '→', icon: null },
                { text: 'Più Vendite', icon: TrendingUp, color: 'amber' },
                { text: '→', icon: null },
                { text: 'Più Aziende', icon: Store, color: 'emerald' }
              ].map((item, i) => (
                item.icon ? (
                  <div key={i} className={`flex items-center gap-2 bg-${item.color}-500/10 border border-${item.color}-500/30 rounded-xl px-4 py-3`}>
                    <item.icon className={`w-5 h-5 text-${item.color}-400`} />
                    <span className="text-white font-medium">{item.text}</span>
                  </div>
                ) : (
                  <span key={i} className="text-slate-500 text-2xl">{item.text}</span>
                )
              ))}
            </div>
            <p className="text-slate-400 mt-6 max-w-2xl mx-auto">
              Un ciclo virtuoso che si autoalimenta. Ogni nuovo elemento accelera la crescita degli altri. 
              <strong className="text-white"> Senza limiti.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ==================== LE 8 APP CUSTOM ==================== */}
      <section className="py-12 md:py-24 animate-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-purple-400 font-medium mb-4 block">APP PERSONALIZZATE</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              8 Nicchie Lanciate, <span className="text-purple-400">Infinite Possibilità</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-3xl mx-auto">
              Ogni app è una soluzione white-label completa: branding del cliente, 
              pubblicata su App Store e Google Play. E questo è solo l&apos;inizio.
            </p>
          </div>

          {/* App Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-12">
            {CUSTOM_APPS.map((app, index) => (
              <a
                key={index}
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`bg-slate-800/30 border border-slate-700/50 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-${app.color}-500/50 transition-all group hover:transform hover:-translate-y-2`}
              >
                <div className={`inline-flex items-center justify-center w-10 h-10 md:w-14 md:h-14 bg-${app.color}-500/10 rounded-xl md:rounded-2xl mb-3 md:mb-4 group-hover:scale-110 transition-transform`}>
                  <app.icon className={`w-5 h-5 md:w-7 md:h-7 text-${app.color}-400`} />
                </div>
                <h3 className="text-base md:text-xl font-bold text-white mb-0.5 md:mb-1">{app.name}</h3>
                <p className="text-slate-400 text-xs md:text-sm mb-2 md:mb-3">{app.niche}</p>
                <div className="flex items-center gap-1 text-emerald-400 text-xs md:text-sm group-hover:gap-2 transition-all">
                  <span>Visita sito</span>
                  <ExternalLink className="w-3 h-3 md:w-4 md:h-4" />
                </div>
              </a>
            ))}
          </div>

          {/* Scalability Message */}
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Qualsiasi Nicchia, Stessa Tecnologia
            </h3>
            <p className="text-slate-300 text-lg max-w-3xl mx-auto mb-6">
              Queste 8 app sono state lanciate negli <strong className="text-white">ultimi mesi</strong>. 
              La tecnologia è modulare: possiamo creare un&apos;app per <strong className="text-purple-400">qualsiasi settore</strong> in 
              poche settimane. Ristoranti, hotel, studi medici, consulenti, artigiani... 
              il mercato è <strong className="text-white">illimitato</strong>.
            </p>
            <div className="inline-flex items-center gap-4 bg-slate-800/50 rounded-xl px-6 py-3">
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-400">5</p>
                <p className="text-slate-400 text-xs">App vendute</p>
              </div>
              <div className="w-px h-10 bg-slate-700"></div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white">∞</p>
                <p className="text-slate-400 text-xs">Nicchie possibili</p>
              </div>
              <div className="w-px h-10 bg-slate-700"></div>
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">€49-149</p>
                <p className="text-slate-400 text-xs">MRR per app</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== I NUMERI ==================== */}
      <section className="py-12 md:py-24 animate-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-emerald-400 font-medium mb-4 block">I RISULTATI</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Crescita Organica, <span className="text-emerald-400">Zero Marketing</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Questi numeri sono stati raggiunti con meno di €500 in pubblicità. 
              Immagina cosa possiamo fare con €400K in marketing strategico.
            </p>
          </div>

          {/* Main Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-12">
            {[
              { value: 2000000, suffix: '+', label: 'Prenotazioni Gestite', icon: Calendar, color: 'emerald' },
              { value: 50000, suffix: '+', label: 'Utenti Registrati', icon: Users, color: 'blue' },
              { value: 10000000, prefix: '€', suffix: '+', label: 'Volume Transato', icon: DollarSign, color: 'purple' },
              { value: 100, suffix: '', label: 'Abbonati Gestionale', icon: Store, color: 'amber' }
            ].map((metric, index) => (
              <div 
                key={index}
                className={`bg-slate-800/30 border border-slate-700/50 rounded-xl md:rounded-2xl p-4 md:p-6 text-center hover:border-${metric.color}-500/50 transition-all`}
              >
                <div className={`inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-${metric.color}-500/10 rounded-lg md:rounded-xl mb-3 md:mb-4`}>
                  <metric.icon className={`w-5 h-5 md:w-6 md:h-6 text-${metric.color}-400`} />
                </div>
                <p className={`text-xl md:text-3xl lg:text-4xl font-bold text-${metric.color}-400 mb-1 md:mb-2`}>
                  <AnimatedCounter end={metric.value} suffix={metric.suffix} prefix={metric.prefix || ''} />
                </p>
                <p className="text-slate-400 text-xs md:text-sm">{metric.label}</p>
              </div>
            ))}
          </div>

          {/* Context Card */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-2xl p-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center md:text-left">
                <h4 className="text-white font-bold mb-2">Fatturato Primo Anno</h4>
                <p className="text-3xl font-bold text-emerald-400 mb-1">€20.000</p>
                <p className="text-slate-400 text-sm">Solo abbonamenti gestionale</p>
              </div>
              <div className="text-center md:text-left">
                <h4 className="text-white font-bold mb-2">Spesa Marketing</h4>
                <p className="text-3xl font-bold text-blue-400 mb-1">&lt; €500</p>
                <p className="text-slate-400 text-sm">Test pubblicitari</p>
              </div>
              <div className="text-center md:text-left">
                <h4 className="text-white font-bold mb-2">ROI Implicito</h4>
                <p className="text-3xl font-bold text-purple-400 mb-1">40x</p>
                <p className="text-slate-400 text-sm">Ritorno sull&apos;investimento</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== UTILIZZO FONDI ==================== */}
      <section className="py-12 md:py-24 bg-slate-900/30 animate-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-emerald-400 font-medium mb-4 block">UTILIZZO DEI FONDI</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              €1M Interamente <span className="text-emerald-400">Reinvestito</span>
            </h2>
            <p className="text-slate-400 text-lg">
              Zero prelievi per i fondatori. Ogni euro va nella crescita.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center">
            <div className="space-y-6 md:space-y-8">
              {[
                { label: 'Marketing & Acquisizione', amount: '€400.000', pct: 40, color: 'emerald', desc: 'Google/Meta Ads, influencer, eventi, partnership' },
                { label: 'Team & Sviluppo', amount: '€300.000', pct: 30, color: 'blue', desc: 'Developer senior, UX designer, sales, support' },
                { label: 'Infrastruttura & Nuovi Prodotti', amount: '€200.000', pct: 20, color: 'purple', desc: 'Server, Calendar Pro, Programma Star, AI' },
                { label: 'Sede & Operations', amount: '€100.000', pct: 10, color: 'amber', desc: 'Nuovo ufficio, legale, amministrazione' }
              ].map((item, i) => (
                <div key={i}>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-1">
                    <div>
                      <span className="text-white font-medium text-sm md:text-base">{item.label}</span>
                      <p className="text-slate-500 text-xs md:text-sm">{item.desc}</p>
                    </div>
                    <span className={`text-${item.color}-400 font-bold text-lg md:text-xl`}>{item.amount}</span>
                  </div>
                  <div className="h-2 md:h-3 bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r from-${item.color}-500 to-${item.color}-400 rounded-full`} style={{ width: `${item.pct}%` }}></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl md:rounded-3xl p-6 md:p-8 text-center">
              <p className="text-slate-400 mb-2 text-sm md:text-base">Investimento Richiesto</p>
              <p className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400 mb-2">
                €1.000.000
              </p>
              <p className="text-slate-400 mb-6 md:mb-8 text-sm md:text-base">per il <span className="text-white font-bold">10%</span> di Skipres Srl</p>
              
              <div className="space-y-2 md:space-y-3 mb-6 md:mb-8 text-left">
                {[
                  '100% reinvestito nella crescita',
                  'Zero prelievi per i fondatori',
                  'Report trimestrale dettagliato',
                  'Seat nel board consultivo',
                  'Comunicazione diretta con il CEO'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 md:gap-3 text-slate-300 text-sm md:text-base">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-emerald-400 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              <a href="#contact-form">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-white py-3 md:py-4">
                  Parliamone Insieme
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== IL FONDATORE ==================== */}
      <section className="py-12 md:py-24 animate-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center">
              <div>
                <span className="text-emerald-400 font-medium mb-4 block">IL FONDATORE</span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Alessandro Soviero</h2>
                <p className="text-slate-300 text-lg mb-6">
                  Imprenditore digitale, ho fondato Skipres con una visione chiara: 
                  rendere la tecnologia accessibile a ogni attività locale. 
                  Dalla grande città al piccolo paese.
                </p>
                <p className="text-slate-300 text-lg mb-8">
                  Non cerco un investitore qualsiasi. Cerco un <strong className="text-white">partner</strong> 
                  {' '}che condivida la visione, che creda nel progetto, che voglia costruire 
                  qualcosa di grande. Insieme.
                </p>
                
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_admin-stripe-dash/artifacts/rkz0uqhg_1567070040746.jpeg" 
                    alt="Alessandro Soviero" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-emerald-500/50"
                  />
                  <div>
                    <p className="text-white font-bold text-lg">Alessandro Soviero</p>
                    <p className="text-slate-400">Founder &amp; CEO, Skipres Srl</p>
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex items-center gap-4">
                  <a 
                    href="https://www.linkedin.com/in/alessandrosoviero" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/30 rounded-lg px-4 py-2 transition-colors"
                  >
                    <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <span className="text-blue-400 text-sm font-medium">LinkedIn</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/alessandrosoviero/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-pink-600/20 hover:bg-pink-600/30 border border-pink-600/30 rounded-lg px-4 py-2 transition-colors"
                  >
                    <svg className="w-5 h-5 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    <span className="text-pink-400 text-sm font-medium">Instagram</span>
                  </a>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -top-4 -left-4 text-8xl text-emerald-500/20 font-serif">&ldquo;</div>
                <blockquote className="text-xl md:text-2xl text-white italic leading-relaxed pl-8">
                  Non vogliamo 30 investitori con €30K ciascuno. Vogliamo 
                  <span className="text-emerald-400"> UN partner </span>
                  che sposi il progetto e insieme scaliamo il mondo.
                </blockquote>
                <p className="text-slate-400 mt-6 pl-8">
                  Cerco qualcuno che guardi oltre i numeri di oggi. 
                  Che veda il potenziale di domani. Che capisca che con il giusto 
                  investimento possiamo trasformare Skipres da startup italiana 
                  a player globale.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FORM CONTATTO ==================== */}
      <section id="contact-form" className="py-12 md:py-24 bg-slate-900/30 animate-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-6 md:mb-12">
            <span className="text-emerald-400 font-medium mb-4 block">INIZIAMO</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Raccontaci di <span className="text-emerald-400">Te</span>
            </h2>
            <p className="text-slate-400 text-lg">
              Compila il form per richiedere una chiamata con Alessandro
            </p>
          </div>

          {submitted ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-3xl p-12 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Richiesta Inviata!</h3>
              <p className="text-slate-300 text-lg">
                Grazie per il tuo interesse. Alessandro ti contatterà personalmente entro 48 ore.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Nome Completo *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-emerald-500 focus:outline-none" placeholder="Mario Rossi" />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-emerald-500 focus:outline-none" placeholder="mario@email.com" />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Telefono *</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input type="tel" required value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-emerald-500 focus:outline-none" placeholder="+39 333 1234567" />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Azienda</label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input type="text" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-emerald-500 focus:outline-none" placeholder="Nome azienda" />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-slate-300 text-sm font-medium mb-2">Sei già imprenditore? *</label>
                <div className="grid grid-cols-2 gap-4">
                  {['Sì, ho altre aziende', 'No, primo investimento'].map((opt) => (
                    <button key={opt} type="button" onClick={() => setFormData({...formData, isEntrepreneur: opt})}
                      className={`p-4 rounded-xl border transition-all ${formData.isEntrepreneur === opt ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-slate-300 text-sm font-medium mb-2">Esperienza tech/startup</label>
                <textarea value={formData.experience} onChange={e => setFormData({...formData, experience: e.target.value})} rows={3}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-emerald-500 focus:outline-none resize-none"
                  placeholder="Raccontaci la tua esperienza..." />
              </div>

              <div className="mb-6">
                <label className="block text-slate-300 text-sm font-medium mb-2">Capacità di investimento *</label>
                <select required value={formData.investmentCapacity} onChange={e => setFormData({...formData, investmentCapacity: e.target.value})}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-emerald-500 focus:outline-none">
                  <option value="">Seleziona un range</option>
                  <option value="100k-250k">€100.000 - €250.000</option>
                  <option value="250k-500k">€250.000 - €500.000</option>
                  <option value="500k-1m">€500.000 - €1.000.000</option>
                  <option value="1m+">Oltre €1.000.000</option>
                </select>
              </div>

              <div className="mb-8">
                <label className="block text-slate-300 text-sm font-medium mb-2">Perché vuoi investire in Skipres? *</label>
                <textarea required value={formData.whyInvest} onChange={e => setFormData({...formData, whyInvest: e.target.value})} rows={4}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-emerald-500 focus:outline-none resize-none"
                  placeholder="Cosa ti ha colpito? Cosa puoi portare oltre al capitale?" />
              </div>

              <Button type="submit" disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-white text-lg py-6 rounded-xl shadow-lg disabled:opacity-50">
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Invio in corso...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="hidden sm:inline">Richiedi Chiamata con Alessandro</span>
                    <span className="sm:hidden">Richiedi Chiamata</span>
                  </span>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img src={LOGOS.dark} alt="Skipres" className="h-6 mx-auto mb-4" />
          <p className="text-slate-500 text-sm">
            © 2025 Skipres Srl • P.IVA 10467151212 • Via Cesare Battisti 43, Striano (NA)
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Invest;
